import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { FC } from 'react'

type FooterColumnLink = {
  label: string
  href: string
  target?: '_blank'
  rel?: 'noopener noreferrer nofollow'
}

type FooterColumn = {
  title: string
  links: FooterColumnLink[]
}

const getFooterColumns = (): FooterColumn[] => [
  {
    title: 'Company',
    links: [
      { label: 'Careers', href: 'https://www.flowcode.com/company/careers' },
      {
        label: 'Team',
        href: 'https://www.flowcode.com/company/team',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'Press',
        href: 'https://www.flowcode.com/company/press',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'Blog',
        href: 'https://www.flowcode.com/company/blog',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      }
    ]
  },
  {
    title: 'Product',
    links: [
      {
        label: 'Contact Support',
        href: 'https://www.flowcode.com/contact-us',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'F2 Help Desk',
        href: 'https://help.flowcode.com/enterprise/en/',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'F1 Help Desk',
        href: 'https://support.flowcode.com/',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'QR Generator',
        href: 'https://www.flowcode.com/free-qr-code-generator',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      }
    ]
  },
  {
    title: 'Terms',
    links: [
      {
        label: 'Privacy Policy',
        href: 'https://www.flowcode.com/terms/privacy-policy',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'Privacy Request',
        href: 'https://www.flowcode.com/terms/privacyrequest',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'Patent Notice',
        href: 'https://www.flowcode.com/terms/patent-notice',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      },
      {
        label: 'Terms of Use',
        href: 'https://www.flowcode.com/terms/terms-of-use',
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      }
    ]
  }
]

export const FooterLink: FC = () => {
  const columns = getFooterColumns()

  return (
    <Box
      component="nav"
      aria-label="Footer"
      sx={theme => ({
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(auto-fit, minmax(${theme.spacing(30)}, 1fr))`,
          lg: `repeat(${columns.length}, ${theme.spacing(35)})`,
          xl: `repeat(${columns.length}, ${theme.spacing(52)})`
        },
        columnGap: { xs: 4.5, xl: 6 },
        rowGap: { xs: 4, xl: 0 },
        width: '100%',
        maxWidth: {
          md: '100%',
          lg: theme.spacing(116),
          xl: theme.spacing(168)
        },
        '& p, li, span': {
          color: theme.palette.surface[1]
        }
      })}
    >
      {columns.map(column => (
        <Stack
          key={column.title}
          sx={{
            width: '100%',
            maxWidth: { md: 116, lg: 140, xl: 208 }
          }}
          gap={2.2}
        >
          <Typography variant="labelLarge">{column.title}</Typography>
          <Stack component="ul" gap={0.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {column.links.map(link => (
              <Box
                key={link.href + link.label}
                component="li"
                sx={{
                  lineHeight: 1.4,
                  span: {
                    fontWeight: 'normal',
                    letterSpacing: 'normal'
                  }
                }}
              >
                <a href={link.href} target={link.target} rel={link.rel}>
                  <Typography variant="labelSmall" component="span">
                    {link.label}
                  </Typography>
                </a>
              </Box>
            ))}
          </Stack>
        </Stack>
      ))}
    </Box>
  )
}
